// FILE: src/components/case-studies/admin/CaseStudyEditor.js
//
// Editor shell (Case Study Question Framework, PR 2). The STAGE BOARD is the
// primary authoring surface — narrative sections, artifacts, and linked
// questions are all authored in stage context, so there is no separate
// "sequence" tab duplicating state. Tabs: Overview | Stage Board |
// Validation | Preview.
//
// Data flow: one source of truth (admin_get_case_full) held here; every
// mutation is an explicit RPC then a reload (pessimistic — no optimistic
// state to roll back). The editor never fetches case_attempt_answer_keys;
// everything it shows comes from admin-authorized rows.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminExportCaseStudy, adminGetCaseFull, publishCaseStudy, validateCaseStudy } from '../../../services/caseStudyService';
import { buildStageModel, downloadJson, tagWarnings } from './caseAdminUtils';
import OverviewPanel from './OverviewPanel';
import StageBoard from './StageBoard';
import ValidationPanel from './ValidationPanel';
import CasePreview from './CasePreview';
import { S, T, statusChip } from './caseAdminStyles';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'board', label: 'Stage Board' },
  { id: 'validation', label: 'Validation' },
  { id: 'preview', label: 'Preview' },
];

export default function CaseStudyEditor({ caseStudyId, onBack, onEditQuestion }) {
  const [full, setFull] = useState(null);
  const [tab, setTab] = useState('board');
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false); // overview form has unsaved edits
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  const reload = useCallback(async () => {
    try { setError(null); setFull(await adminGetCaseFull(caseStudyId)); }
    catch (e) { setError(e.message); }
  }, [caseStudyId]);
  useEffect(() => { reload(); }, [reload]);

  // Unsaved-change protection: block tab switches and Back while the
  // overview form is dirty, and warn on browser unload.
  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const guardedNav = useCallback((fn) => {
    if (dirty && !window.confirm('You have unsaved overview changes. Discard them?')) return;
    setDirty(false);
    fn();
  }, [dirty]);

  const stageModel = useMemo(() => (full ? buildStageModel(full) : []), [full]);
  const warnings = useMemo(() => (full ? tagWarnings(full.case.tags) : []), [full]);

  async function doPublish() {
    setPublishing(true); setError(null);
    try {
      const res = await publishCaseStudy(caseStudyId);
      setPublishResult(res);
      await reload();
      setTab('validation');
    } catch (e) {
      setError(e.message);
      // Surface the gate's actionable messages in the Validation tab.
      try { setPublishResult({ validation: await validateCaseStudy(caseStudyId) }); } catch (_) { /* noop */ }
      setTab('validation');
    } finally { setPublishing(false); }
  }

  if (!full) {
    return (
      <div style={S.page} data-testid="case-editor-loading">
        {error ? <div style={S.err}>{error}</div> : <div style={S.sub}>Loading case…</div>}
        <button style={S.btn} onClick={onBack}>← Back</button>
      </div>
    );
  }

  const cs = full.case;
  return (
    <div style={S.page} data-testid="case-editor">
      <div style={S.headerRow}>
        <button style={S.btn} data-testid="case-editor-back" onClick={() => guardedNav(onBack)}>← All cases</button>
        <div style={{ minWidth: 220 }}>
          <h1 style={S.h1}>{cs.title}</h1>
          <div style={S.sub}>
            {(cs.certification || '').toUpperCase()} · v{cs.version} · <span style={statusChip(cs.status)}>{cs.status}</span>
            {warnings.length > 0 && (
              <span style={{ ...S.chip(T.warnSoft, '#8a6116'), marginLeft: 8 }} title={warnings.join('\n')}>
                {warnings.length} advisory
              </span>
            )}
          </div>
        </div>
        <span style={{ flex: 1 }} />
        <button style={S.btn} data-testid="case-export-button"
          onClick={async () => {
            try {
              const bundle = await adminExportCaseStudy(caseStudyId);
              downloadJson(`case-${cs.slug || caseStudyId}.json`, bundle);
            } catch (e) { setError(e.message); }
          }}>
          ⇩ Export JSON
        </button>
        <button style={S.btnPrimary} data-testid="case-publish-button" disabled={publishing} onClick={doPublish}>
          {publishing ? 'Publishing…' : (cs.status === 'published' ? 'Republish (bumps version)' : 'Publish')}
        </button>
      </div>

      {error && <div style={S.err} data-testid="case-editor-error">{error}</div>}

      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {TABS.map((t) => (
          <button key={t.id} data-testid={`case-tab-${t.id}`}
            style={{ ...S.btn, ...(tab === t.id ? { background: T.brand, borderColor: T.brand, color: '#fff' } : {}) }}
            onClick={() => guardedNav(() => setTab(t.id))}>
            {t.label}
            {t.id === 'overview' && dirty ? ' •' : ''}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <OverviewPanel full={full} onDirtyChange={setDirty} onSaved={reload} />
      )}
      {tab === 'board' && (
        <StageBoard full={full} stageModel={stageModel} onChanged={reload}
          onEditQuestion={onEditQuestion
            ? (qid) => onEditQuestion(qid, cs.certification, caseStudyId)
            : undefined} />
      )}
      {tab === 'validation' && (
        <ValidationPanel caseStudyId={caseStudyId} full={full} publishResult={publishResult} />
      )}
      {tab === 'preview' && (
        <CasePreview full={full} />
      )}
    </div>
  );
}
